import './sentry';  // init crash reporting before anything else
import './lib/polyfills';  // install global runtime polyfills before any code runs
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { isMobile as checkMobile, isTauri } from '@/platform';
import { fePerf } from '@/lib/fePerf';
import { IconStrokeProvider } from '@/lib/IconStrokeProvider';

// Dev: log everything at debug+ and forward through the Rust terminal
// by default. See AGENTS.md → "Debugging the JS side from the Rust
// terminal".
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__SONGBIRD_LOG__ = 'debug';
  window.__SONGBIRD_LOG_REMOTE__ = 'debug';
}

fePerf('main.tsx top');

// Long-task observer: any main-thread block longer than 50ms shows up
// as a [fe-perf] line so post-load responsiveness regressions are
// visible in the same timeline that frames the boot phases. The
// PerformanceObserver API itself is cheap (one entry per long task,
// no per-frame cost), so this stays installed for the life of the
// session. Safari/older WebKit may not support `longtask` — wrap in
// try/catch so we don't break boot on unsupported runtimes.
try {
  if (typeof PerformanceObserver !== 'undefined') {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        fePerf(`longtask: ${entry.duration.toFixed(0)}ms (${entry.name || 'unknown'})`);
      }
    });
    observer.observe({ entryTypes: ['longtask'] });
  }
} catch {
  // longtask entryType not supported — skip silently.
}


let loadingRoot: ReturnType<typeof createRoot> | null = null;

// Tear down the build-time picker splash + its inline style. Called
// from any code path that needs `#root` to be visible (loading screen,
// error UI, real picker mount). Idempotent.
function removeSplash() {
  document.getElementById('splash')?.remove();
  document.getElementById('splash-style')?.remove();
}

function renderLoadingScreen(message: string, progress?: { current: number; total: number; fileName?: string }) {
  // The splash assumes we'll land on the project picker; WASM/clone
  // flows that take over with a custom loading screen need to evict it
  // now so their message becomes visible.
  removeSplash();
  const el = document.getElementById('root')!;
  if (!loadingRoot) loadingRoot = createRoot(el);
  const pct = progress && progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : null;
  loadingRoot.render(
    <div className="absolute inset-0 z-100 flex items-center justify-center bg-[hsl(var(--background))]" data-tauri-drag-region>
      <div className="flex flex-col items-center space-y-6 max-w-lg w-full text-center px-8">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight text-[hsl(var(--foreground))]">
            Tivra
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] opacity-70">
            {message}
          </p>
        </div>
        {pct !== null ? (
          <div className="w-64 flex flex-col items-center space-y-2">
            <div className="w-full h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
              <div
                className="h-full rounded-full bg-[hsl(var(--foreground))] transition-all duration-200"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] opacity-60 truncate max-w-full">
              {progress!.current}/{progress!.total} samples{progress!.fileName ? ` — ${progress!.fileName}` : ''}
            </p>
          </div>
        ) : (
          <div className="w-8 h-8 border-2 border-[hsl(var(--muted))] border-t-[hsl(var(--foreground))] rounded-full animate-spin" />
        )}
      </div>
    </div>
  );
}

async function bootstrap() {
  // --- WASM Bridge (must run BEFORE juce.js imports) ---
  // Activate if: ?wasm query param, VITE_WASM_MODE=true env var
  // (baked into production builds by release-wasm.sh), OR runtime
  // detection finds a WASM manifest on an HTTPS-served site.
  if (!window.__SONGBIRD__) {
    const urlParams = new URLSearchParams(location.search);
    let isWasmMode = urlParams.has('wasm')
      || import.meta.env.VITE_WASM_MODE === 'true';

    // Auto-detect WASM on HTTPS sites (e.g. daw.audio on Cloudflare
    // Pages) when the build-time env var wasn't set. Probe for the
    // manifest only when there's no explicit ?ws= param (which signals
    // a headless/Cloud Run deployment that needs WebSocket mode).
    if (!isWasmMode && location.protocol === 'https:' && !urlParams.has('ws')) {
      try {
        const probe = await fetch('/wasm-manifest.json', { method: 'HEAD' });
        if (probe.ok && probe.headers.get('content-type')?.includes('json')) {
          console.log('[main] WASM manifest found via auto-detection');
          isWasmMode = true;
        }
      } catch { /* no manifest — fall through to WS mode */ }
    }

    if (isWasmMode) {
      // Mobile-web visitors get the desktop-only notice BEFORE the multi-MB
      // WASM engine downloads — showing it after wasted the whole download on
      // a screen we don't support yet (TIV-184). The `?mode=mobile` override
      // keeps the mobile UI testable on web (TIV-177). `checkMobile()` also
      // returns true for `?mode=mobile`, so exclude it explicitly. `urlParams`
      // is the URLSearchParams already parsed at the top of this block.
      if (checkMobile() && urlParams.get('mode') !== 'mobile') {
        removeSplash();
        const { DesktopOnlyNotice } = await import('./components/mobile/DesktopOnlyNotice.tsx');
        createRoot(document.getElementById('root')!).render(
          <StrictMode>
            <IconStrokeProvider>
              <DesktopOnlyNotice />
            </IconStrokeProvider>
          </StrictMode>
        );
        return;
      }
      // Set the flag BEFORE any dynamic imports so getPlatform() caches
      // 'wasm' instead of 'headless' when modules in the import chain
      // call isWasm() during evaluation.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__SONGBIRD_WASM_MODE__ = true;
      // Dev: mirror the browser console into the Vite terminal (there's no
      // terminal-side Rust process on WASM to forward through). Installed
      // before the bridge imports so engine-boot logs are captured too.
      if (import.meta.env.DEV) {
        const { installDevTerminalLog } = await import('./lib/devTerminalLog');
        installDevTerminalLog();
      }
      try {
        renderLoadingScreen('Loading WASM engine...');
        // Install the stable backend shell BEFORE importing the bridge, so
        // any listener bound while the bridge's module graph evaluates
        // (promiseHandler reply handler, store subscriptions) attaches to the
        // registry connectWasmBridge will adopt. See wasmBackendShell.ts.
        const { installWasmBackendShell } = await import('./data/wasmBackendShell');
        installWasmBackendShell();
        const { connectWasmBridge } = await import('./data/wasmBridge');
        await connectWasmBridge();
        console.log('[main] WASM bridge connected');
      } catch (e) {
        console.error('[main] WASM bridge failed:', e);
        loadingRoot!.render(
          <div className="absolute inset-0 z-100 flex items-center justify-center bg-[hsl(var(--background))]">
            <div className="flex flex-col items-center space-y-10 max-w-lg w-full text-center px-8">
              <div className="space-y-4">
                <h1 className="text-4xl font-bold tracking-tight text-[hsl(var(--foreground))]">
                  WASM Engine Failed
                </h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))] opacity-70 max-w-sm mx-auto">
                  Could not load the WebAssembly audio engine. Make sure you ran build-wasm.sh first.
                </p>
              </div>
              <div className="type-readout-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-3 py-1.5 rounded-md truncate max-w-xs break-all">
                {String((e as Error).message)}
              </div>
              <button
                onClick={() => location.reload()}
                className="mt-2 px-4 py-2 text-sm rounded-md bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          </div>
        );
        return;
      }
    }
  }

  // --- WebSocket Bridge (must run BEFORE juce.js imports) ---
  // Connect if: no native Tauri host AND a WS URL is provided via query param, env,
  // or the page is served over HTTPS (reverse-proxied headless mode).
  if (!window.__SONGBIRD__) {
    const wsUrl =
      new URLSearchParams(location.search).get('ws') ||
      (import.meta.env.VITE_WS_URL as string | undefined);
    // Auto-detect WS mode when served over HTTPS (e.g. songbird.toshbox.dev)
    const isHttps = location.protocol === 'https:';
    if (wsUrl || isHttps) {
      const { connectWsBridge } = await import('./data/wsBridge');
      // When served over HTTPS, use the Vite proxy path /ws (wss://)
      // to avoid mixed-content blocking.
      let fullUrl: string;
      if (isHttps) {
        // Pass API key on the WebSocket URL so nginx can authenticate the upgrade
        const apiKey = new URLSearchParams(location.search).get('api_key') || '';
        const wsQuery = apiKey ? `?api_key=${apiKey}` : '';
        fullUrl = `wss://${location.host}/ws${wsQuery}`;
      } else {
        fullUrl = wsUrl!.startsWith('ws') ? wsUrl! : `ws://${wsUrl}`;
      }
      // Set global flag so isWsMode() works even without ?ws= query param
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__SONGBIRD_WS_MODE__ = true;

      // --- Clone repo if ?repo= is present (before WS connect) ---
      const urlParams = new URLSearchParams(location.search);
      const repoUrl = urlParams.get('repo');
      if (repoUrl) {
        try {
          renderLoadingScreen('Downloading project...');
          const apiKey = urlParams.get('api_key') || '';
          const cloneEndpoint = isHttps
            ? `${location.origin}/api/clone${apiKey ? `?api_key=${apiKey}` : ''}`
            : `/api/clone`;

          const cloneRes = await fetch(cloneEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: repoUrl }),
          });
          if (!cloneRes.ok) {
            const text = await cloneRes.text();
            let msg = 'Clone failed';
            try { msg = JSON.parse(text).error || msg; } catch { /* ignore parse errors */ }
            throw new Error(msg);
          }

          // Read NDJSON progress stream
          const reader = cloneRes.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let clonePath = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop()!; // keep incomplete line in buffer
            for (const line of lines) {
              if (!line.trim()) continue;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              let evt: any;
              try { evt = JSON.parse(line); } catch {
                console.warn('[main] Failed to parse clone event:', line);
                continue;
              }
              console.log('[main] clone progress:', evt);
              if (evt.type === 'status') {
                renderLoadingScreen(evt.message);
              } else if (evt.type === 'progress') {
                renderLoadingScreen(
                  `Downloading samples...`,
                  { current: evt.current, total: evt.total, fileName: evt.fileName }
                );
              } else if (evt.type === 'done') {
                clonePath = evt.path;
              } else if (evt.type === 'error') {
                throw new Error(evt.error);
              }
            }
          }

          if (!clonePath) throw new Error('No project path returned from clone service');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).__SONGBIRD_CLONE_PATH__ = clonePath;
        } catch (e) {
          console.error('[main] Clone failed:', e);
          createRoot(document.getElementById('root')!).render(
            <div className="absolute inset-0 z-100 flex items-center justify-center bg-[hsl(var(--background))]">
              <div className="flex flex-col items-center space-y-10 max-w-lg w-full text-center px-8">
                <div className="space-y-4">
                  <h1 className="text-4xl font-bold tracking-tight text-[hsl(var(--foreground))]">
                    Clone Failed
                  </h1>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] opacity-70 max-w-sm mx-auto">
                    Could not clone the project repository.
                  </p>
                </div>
                <div className="type-readout-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-3 py-1.5 rounded-md truncate max-w-xs break-all">
                  {String((e as Error).message)}
                </div>
                <button
                  onClick={() => location.reload()}
                  className="mt-2 px-4 py-2 text-sm rounded-md bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:opacity-90 transition-opacity"
                >
                  Try Again
                </button>
              </div>
            </div>
          );
          return;
        }
      }

      try {
        if (repoUrl) renderLoadingScreen('Connecting to DAW engine...');
        await connectWsBridge(fullUrl);
        // The sync engine initializes as a module side-effect (wiring.ts)
        // before connectWsBridge installs window.__SONGBIRD__, so the
        // WebSocketTransport's backend listeners were deferred. Replay them now.
        const { getSyncEngine } = await import('./sync/wiring');
        const eng = getSyncEngine();
        if (eng) {
          const wst = eng.getTransport('websocket');
          if (wst && 'wireBackendListeners' in wst) {
            (wst as import('./sync/transports/websocket').WebSocketTransport).wireBackendListeners();
          }
        }
      } catch (e) {
        console.error('[main] WS bridge connection failed:', e);
        // Pure-WS mode (no ?repo=) skipped renderLoadingScreen, so the
        // splash still covers #root. Evict it before the error UI mounts.
        removeSplash();
        createRoot(document.getElementById('root')!).render(
          <div className="absolute inset-0 z-100 flex items-center justify-center bg-[hsl(var(--background))]">
            <div className="flex flex-col items-center space-y-10 max-w-lg w-full text-center px-8">
              <div className="space-y-4">
                <h1 className="text-4xl font-bold tracking-tight text-[hsl(var(--foreground))]">
                  Backend Unreachable
                </h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))] opacity-70 max-w-sm mx-auto">
                  Could not connect to the Tivra Native Server headless instance.
                </p>
              </div>
              <div className="type-readout-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-3 py-1.5 rounded-md truncate max-w-xs break-all">
                {fullUrl}
              </div>
              <button
                onClick={() => location.reload()}
                className="mt-2 px-4 py-2 text-sm rounded-md bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          </div>
        );
        return; // Stop initialization
      }
    }
  }

  // Resolve which root component this window needs before touching any
  // imports. Mobile and plugin-popout modules pull in their own panel
  // trees; loading all three on every boot meant the desktop main window
  // paid for two unused module graphs.
  const urlParams = new URLSearchParams(window.location.search);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popout = (window as any).__SONGBIRD_POPOUT__ as
    | { mode?: string; pluginType?: string; trackId?: string; pluginId?: string }
    | undefined;
  const mode = popout?.mode ?? urlParams.get('mode');
  const isMobile = mode === 'mobile' || checkMobile();

  // Kick off theme + settings hydration in the background. Neither is
  // load-bearing for first paint:
  //   - The synchronous `applyThemeEarly` block in index.html already
  //     toggled `html.dark` / `html.light` from the localStorage mirror,
  //     so first paint is correct. `initTheme()` just reconciles with
  //     the Rust-side preference, which in practice matches the mirror.
  //   - `initSettings()` populates the Zustand store with user prefs
  //     (middleCOctave, vibeMode, etc.) that have sensible defaults.
  //     Components subscribed to the store re-render when each setting
  //     arrives.
  // Awaiting these used to be the single biggest chunk of cold-boot
  // latency (10+ serial IPC roundtrips in `initSettings`).
  fePerf('background init: theme + settings kicked off');
  void (async () => {
    const [{ initTheme }, { useGlobalSettings }, { hydrateUIFixerStore }] = await Promise.all([
      import('./lib/theme'),
      import('./data/settingsStore'),
      import('./data/uiFixerStore'),
    ]);
    void initTheme();
    void useGlobalSettings.getState().initSettings();
    void hydrateUIFixerStore();
  })();

  // Import only the root component this window actually needs. `mode`
  // is set by `popout.rs`'s initialization_script for Tauri popouts;
  // `checkMobile()` handles the mobile webview path.
  let rootElement: React.ReactElement;
  if (import.meta.env.DEV && urlParams.has('undoProto')) {
    // Dev-only visual harness for the forward-revert undo prototype
    // (TIV-178). Gated on DEV so production bundles never ship it.
    const { UndoProtoHarness } = await import('./components/panels/history/UndoProtoHarness.tsx');
    rootElement = <UndoProtoHarness />;
  } else if (mode === 'plugin') {
    const { StandalonePluginPanel } = await import('./components/plugins/StandalonePluginPanel.tsx');
    rootElement = <StandalonePluginPanel />;
  } else if (isMobile) {
    const { MobileApp } = await import('./components/mobile/MobileApp.tsx');
    rootElement = <MobileApp />;
  } else {
    // Seed the whole-app zoom from the viewport before first paint so the UI
    // doesn't render too-small-then-jump on a cold boot. Best-effort and
    // synchronous (no IPC); `initSettings` reconciles with a saved `uiZoom`
    // preference once the bulk load resolves. Native zooms documentElement;
    // web routes through bootAppZoomWeb, which zooms documentElement on Safari
    // (WebKit reflows vw/vh, like native) and counter-sizes #root on
    // Blink/Gecko (which otherwise overflow the viewport).
    const { appZoomEnabled, applyAppZoom, bootAppZoomWeb, seedAppZoomDefault } = await import('./lib/appZoom');
    if (appZoomEnabled()) applyAppZoom(seedAppZoomDefault());
    else bootAppZoomWeb(seedAppZoomDefault());
    const { default: App } = await import('./App.tsx');
    rootElement = <App />;
    // Hold the (still-visible) splash spinner until the picker's display
    // faces are decoded, so its first paint is already-fonted instead of
    // flashing the system-ui fallback and swapping a frame later. Timeout-
    // raced so a slow font fetch can't pin the spinner. Picker path only —
    // the splash overlay lives in `index.html` (main window).
    const { awaitPickerFonts } = await import('./lib/theme/fonts');
    await awaitPickerFonts();
    fePerf('picker fonts ready');
  }
  fePerf('root component module loaded');

  // If a loading screen was shown (clone flow), unmount it first
  const rootEl = document.getElementById('root')!;
  const appRoot = loadingRoot || createRoot(rootEl);
  loadingRoot = null;

  // Remove the build-time splash (injected by `pickerSplashPlugin` in
  // vite.config.ts) in the same synchronous task as `appRoot.render` —
  // both DOM ops complete before the browser's next paint, so the user
  // sees rootElement (picker, autoload LoadingScreen, etc.) on the
  // first frame with no splash-on-top gap. Earlier code deferred this
  // to two RAFs after render to be defensive about WebKit paint
  // ordering, but that left the splash sitting over progress events
  // that fired between render and the second RAF.
  removeSplash();
  appRoot.render(
    <StrictMode>
      <IconStrokeProvider>
        {rootElement}
      </IconStrokeProvider>
    </StrictMode>
  );
  fePerf('React render called');

  // WebView blackout recovery: heartbeat + resume repaint for the desktop
  // main window. Pairs with the native watchdog (webview_watchdog.rs) that
  // reloads the WKWebView if it goes dark after a sleep / battery-drain
  // wake. Main window only — a popout's heartbeat would mask a dead main
  // window. Skipped in browser (WS/WASM) modes, which have no native host.
  if (isTauri() && mode !== 'plugin' && !isMobile) {
    void import('./lib/recovery/webviewRecovery').then((m) => m.installWebviewRecovery());
  }

  // --- Auto-open cloned project ---
  // If we cloned a repo via ?repo=, open it now that the bridge is connected.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clonePath = (window as any).__SONGBIRD_CLONE_PATH__;
  if (clonePath) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (window as any).__SONGBIRD_CLONE_PATH__;
    const { send } = await import('./sync/api');
    // Small delay to let the headless engine finish its init handshake
    setTimeout(() => {
      console.log('[main] Auto-opening cloned project:', clonePath);
      send('project.choice', { choice: `openRecent:${clonePath}` });
    }, 500);
  }
}

bootstrap().catch(console.error);
